import { yupResolver } from "@hookform/resolvers/yup";
import { format, parse, isValid } from "date-fns";
import PropTypes from "prop-types";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-styled-flexboxgrid";
import { useTheme } from "styled-components";
import * as yup from "yup";
import Avatar from "../../components/Avatar";
import Box from "../../components/Box";
import Button from "../../components/Button";
import ButtonGroup from "../../components/ButtonGroup";
import DatePicker from "../../components/DatePicker";
import Divider from "../../components/Divider";
import ExpansionPanel from "../../components/ExpansionPanel";
import Form from "../../components/Form";
import FormLabel from "../../components/FormLabel";
import Icon from "../../components/Icon";
import Input from "../../components/Input";
import InputTags from "../../components/InputTags";
import Loader from "../../components/Loader";
import Paper from "../../components/Paper";
import Typography from "../../components/Typography";
import { FORM } from "../../utils/constans";
import { makeStyles } from "../../utils/styles";

const useStyles = makeStyles((css) => ({
    paper: css`
        position: relative;
        overflow: hidden;
        padding: 40px 40px 15px 40px;
        width: 940px;
    `,
    title: css`
        margin-bottom: 35px;
    `,
    divider: css`
        margin: 25px 0;
    `,
    dividerFooter: css`
        margin: 5px 0 15px 0;
    `,
    buttons: css`
        display: flex;
        justify-content: flex-end;
    `,
    fontOne: css`
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 20px;
    `,
    fontFour: css`
        font-size: 14px;
        font-weight: 600;
        margin-top: 20px;
        margin-bottom: 20px;
    `,
}));

const schemaBasic = {
    first_name: yup.string().required(FORM.FIELD_IS_REQUIRED),
    last_name: yup.string().required(FORM.FIELD_IS_REQUIRED),
    dob: yup.string().required(FORM.FIELD_IS_REQUIRED).nullable(),
    birth_place: yup.string().required(FORM.FIELD_IS_REQUIRED),
    nationality: yup.string().required(FORM.FIELD_IS_REQUIRED),
    bank_account_country: yup.string().required(FORM.FIELD_IS_REQUIRED),
    bank_account: yup.string().required(FORM.FIELD_IS_REQUIRED),
    health_book_expires: yup
        .string()
        .required(FORM.FIELD_IS_REQUIRED)
        .nullable(),
    phone: yup.string().required(FORM.FIELD_IS_REQUIRED),
    phone_prefix: yup.string().required(FORM.FIELD_IS_REQUIRED),
};

const schemaEdit = yup.object().shape({
    ...schemaBasic,
});

const FormWorker = ({
    loading,
    initialValues = {},
    languages,
    professions,
    onSubmit,
    onClickCancel,
}) => {
    const { t } = useTranslation();

    const styles = useStyles();
    const theme = useTheme();

    const { register, handleSubmit, errors, control } = useForm({
        resolver: yupResolver(schemaEdit),
        defaultValues: {
            ...initialValues,
        },
    });

    return (
        <Paper customStyle={styles.paper}>
            <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <Typography customStyle={styles.title} as="h1">
                    {t("Edytuj użytkownika")}
                </Typography>
                <Divider customStyle={styles.divider} />
                <Row>
                    <Col xs={6}>
                        <Box customStyle={styles.boxLeft}>
                            {initialValues.avatarUrl && (
                                <Box customStyle={styles.avatar}>
                                    <Avatar
                                        src={initialValues.avatarUrl}
                                        size="large"
                                    />
                                </Box>
                            )}
                            <Input
                                label={t("Imię")}
                                name="first_name"
                                ref={register}
                                error={!!errors.first_name}
                                errorText={errors.first_name?.message}
                            />
                            <Input
                                label={t("Nazwisko")}
                                name="last_name"
                                ref={register}
                                error={!!errors.last_name}
                                errorText={errors.last_name?.message}
                            />
                            <Input
                                label={t("PESEL")}
                                name={"national_id"}
                                ref={register}
                                error={!!errors.national_id}
                                errorText={errors.national_id?.message}
                            />

                            <FormLabel>{t("Data urodzenia")}</FormLabel>
                            <Controller
                                control={control}
                                name="dob"
                                render={({ onChange, value }) => {
                                    return (
                                        <DatePicker
                                            selected={
                                                value
                                                    ? parse(
                                                          value,
                                                          "dd-MM-yyyy",
                                                          new Date()
                                                      )
                                                    : null
                                            }
                                            onChange={(val) => {
                                                onChange(
                                                    isValid(val)
                                                        ? format(
                                                              val,
                                                              "dd-MM-yyyy"
                                                          )
                                                        : val
                                                );
                                            }}
                                            error={!!errors.dob}
                                            errorText={errors.dob?.message}
                                        />
                                    );
                                }}
                            />

                            <Input
                                label={t("Miejsce urodzenia")}
                                name={"birth_place"}
                                ref={register}
                                error={!!errors.birth_place}
                                errorText={errors.birth_place?.message}
                            />
                            <Input
                                label={t("Obywatelstwo")}
                                name={"nationality"}
                                ref={register}
                                error={!!errors.nationality}
                                errorText={errors.nationality?.message}
                            />
                            <Typography customStyle={styles.fontOne}>
                                {t("Dane finansowe")}
                            </Typography>
                            <Input
                                label={t("Numer konta")}
                                name={"bank_account"}
                                ref={register}
                                error={!!errors.bank_account}
                                errorText={errors.bank_account?.message}
                            />
                            <Input
                                label={t("Kraj banku")}
                                name={"bank_account_country"}
                                ref={register}
                                error={!!errors.bank_account_country}
                                errorText={errors.bank_account_country?.message}
                            />
                            <Typography customStyle={styles.fontOne}>
                                {t("Zawody wykonywane przez pracownika")}
                            </Typography>
                            <Controller
                                control={control}
                                name="professions"
                                render={({ onChange, value }) => {
                                    if (!professions) return null;
                                    const handleAddLanguage = ({ tag }) =>
                                        tag ? onChange([...value, tag]) : null;
                                    const handleRemoveLanguage = ({
                                        id: _id,
                                    }) =>
                                        onChange(
                                            value.filter(({ id }) => id !== _id)
                                        );
                                    const hints = professions.filter(
                                        ({ id }) =>
                                            !value
                                                .map(({ id }) => id)
                                                .includes(id)
                                    );
                                    return (
                                        <InputTags
                                            id="professions"
                                            placeholder="Wpisz zawód i naciśnij Enter"
                                            tags={value}
                                            tagsHints={hints}
                                            useHintsToFindTag
                                            isReturnNewTag
                                            onClickAdd={handleAddLanguage}
                                            onClickDelete={handleRemoveLanguage}
                                            onEnterKeyDown={handleAddLanguage}
                                            enableEnterKeyDown
                                        />
                                    );
                                }}
                            />
                        </Box>
                    </Col>
                    <Col xs={6}>
                        <Box customStyle={styles.boxRight}>
                            <Typography customStyle={styles.fontOne}>
                                {t(
                                    "Data ważności badań sanitaro-epidemiologicznych"
                                )}
                            </Typography>
                            <Controller
                                control={control}
                                name="health_book_expires"
                                render={({ onChange, value }) => (
                                    <DatePicker
                                        selected={
                                            value
                                                ? parse(
                                                      value,
                                                      "dd-MM-yyyy",
                                                      new Date()
                                                  )
                                                : null
                                        }
                                        onChange={(val) => {
                                            onChange(
                                                isValid(val)
                                                    ? format(val, "dd-MM-yyyy")
                                                    : val
                                            );
                                        }}
                                        error={!!errors.health_book_expires}
                                        errorText={
                                            errors.health_book_expires?.message
                                        }
                                    />
                                )}
                            />

                            <Typography customStyle={styles.fontOne}>
                                {t("Dane kontaktowe")}
                            </Typography>
                            <Input
                                label={t("Adres email")}
                                name={"email"}
                                ref={register}
                                readonly
                            />
                            <Row bottom="xs">
                                <Col xs={12}>
                                    <FormLabel>{t("Telefon")}</FormLabel>
                                </Col>
                                <Col xs={2}>
                                    <Input
                                        name="phone_prefix"
                                        ref={register}
                                        error={!!errors.phone_prefix}
                                        errorText={errors.phone_prefix?.message}
                                    />
                                </Col>
                                <Col xs={10}>
                                    <Input
                                        name="phone"
                                        ref={register}
                                        error={!!errors.phone}
                                        errorText={errors.phone?.message}
                                    />
                                </Col>
                            </Row>
                            <Typography customStyle={styles.fontOne}>
                                {t("Adres zamieszkania")}
                            </Typography>
                            <Row bottom="xs">
                                <Col xs={12}>
                                    <Input
                                        label={t("Ulica")}
                                        name="address_live.street"
                                        ref={register}
                                        error={!!errors.address_live?.street}
                                        errorText={
                                            errors.address_live?.street?.message
                                        }
                                    />
                                </Col>
                                <Col xs={7}>
                                    <Input
                                        label={t("Numer domu")}
                                        name="address_live.number"
                                        ref={register}
                                        error={!!errors.address_live?.number}
                                        errorText={
                                            errors.address_live?.number?.message
                                        }
                                    />
                                </Col>
                                <Col xs={5}>
                                    <Input
                                        label={t("Numer lokalu")}
                                        name="address_live.flat"
                                        ref={register}
                                        error={!!errors.address_live?.flat}
                                        errorText={
                                            errors.address_live?.flat?.message
                                        }
                                    />
                                </Col>
                                <Col xs={4}>
                                    <Input
                                        label={t("Kod pocztowy")}
                                        name="address_live.post_code"
                                        ref={register}
                                        error={!!errors.address_live?.post_code}
                                        errorText={
                                            errors.address_live?.post_code
                                                ?.message
                                        }
                                    />
                                </Col>
                                <Col xs={8}>
                                    <Input
                                        label={t("Miejscowość")}
                                        name="address_live.city"
                                        ref={register}
                                        error={!!errors.address_live?.city}
                                        errorText={
                                            errors.address_live?.city?.message
                                        }
                                    />
                                </Col>
                            </Row>
                            <ExpansionPanel title={t("Adres zameldowania")}>
                                <Row bottom="xs">
                                    <Col xs={12}>
                                        <Input
                                            label={t("Ulica")}
                                            name="address_registered.street"
                                            ref={register}
                                            error={
                                                !!errors.address_registered
                                                    ?.street
                                            }
                                            errorText={
                                                errors.address_registered
                                                    ?.street?.message
                                            }
                                        />
                                    </Col>
                                    <Col xs={7}>
                                        <Input
                                            label={t("Numer domu")}
                                            name="address_registered.number"
                                            ref={register}
                                            error={
                                                !!errors.address_registered
                                                    ?.number
                                            }
                                            errorText={
                                                errors.address_registered
                                                    ?.number?.message
                                            }
                                        />
                                    </Col>
                                    <Col xs={5}>
                                        <Input
                                            label={t("Numer lokalu")}
                                            name="address_registered.flat"
                                            ref={register}
                                            error={
                                                !!errors.address_registered
                                                    ?.flat
                                            }
                                            errorText={
                                                errors.address_registered?.flat
                                                    ?.message
                                            }
                                        />
                                    </Col>
                                    <Col xs={4}>
                                        <Input
                                            label={t("Kod pocztowy")}
                                            name="address_registered.post_code"
                                            ref={register}
                                            error={
                                                !!errors.address_registered
                                                    ?.post_code
                                            }
                                            errorText={
                                                errors.address_registered
                                                    ?.post_code?.message
                                            }
                                        />
                                    </Col>
                                    <Col xs={8}>
                                        <Input
                                            label={t("Miejscowość")}
                                            name="address_registered.city"
                                            ref={register}
                                            error={
                                                !!errors.address_registered
                                                    ?.city
                                            }
                                            errorText={
                                                errors.address_registered?.city
                                                    ?.message
                                            }
                                        />
                                    </Col>
                                </Row>
                            </ExpansionPanel>
                            <ExpansionPanel title={t("Adres korespondencyjny")}>
                                <Row bottom="xs">
                                    <Col xs={12}>
                                        <Input
                                        label={t("Ulica")}
                                            name="address_post.street"
                                            ref={register}
                                            error={
                                                !!errors.address_post?.street
                                            }
                                            errorText={
                                                errors.address_post?.street
                                                    ?.message
                                            }
                                        />
                                    </Col>
                                    <Col xs={7}>
                                        <Input
                                        label={t("Numer domu")}
                                            name="address_post.number"
                                            ref={register}
                                            error={
                                                !!errors.address_post?.number
                                            }
                                            errorText={
                                                errors.address_post?.number
                                                    ?.message
                                            }
                                        />
                                    </Col>
                                    <Col xs={5}>
                                        <Input
                                        label={t("Numer lokalu")}
                                            name="address_post.flat"
                                            ref={register}
                                            error={!!errors.address_post?.flat}
                                            errorText={
                                                errors.address_post?.flat
                                                    ?.message
                                            }
                                        />
                                    </Col>
                                    <Col xs={4}>
                                        <Input
                                        label={t("Kod pocztowy")}
                                            name="address_post.post_code"
                                            ref={register}
                                            error={
                                                !!errors.address_post?.post_code
                                            }
                                            errorText={
                                                errors.address_post?.post_code
                                                    ?.message
                                            }
                                        />
                                    </Col>
                                    <Col xs={8}>
                                        <Input
                                            label={t("Miejscowość")}
                                            name="address_post.city"
                                            ref={register}
                                            error={!!errors.address_post?.city}
                                            errorText={
                                                errors.address_post?.city
                                                    ?.message
                                            }
                                        />
                                    </Col>
                                </Row>
                            </ExpansionPanel>
                        </Box>
                        <Box customStyle={styles.marginTop}>
                            <Typography customStyle={styles.fontFour}>
                                {t("Znajomość języków pracownika")}
                            </Typography>
                            <Controller
                                control={control}
                                name="languages"
                                render={({ onChange, value }) => {
                                    if (!languages) return null;
                                    const handleAddLanguage = ({ tag }) =>
                                        tag ? onChange([...value, tag]) : null;
                                    const handleRemoveLanguage = ({
                                        id: _id,
                                    }) =>
                                        onChange(
                                            value.filter(({ id }) => id !== _id)
                                        );
                                    const hints = languages.filter(
                                        ({ id }) =>
                                            !value
                                                .map(({ id }) => id)
                                                .includes(id)
                                    );
                                    return (
                                        <InputTags
                                            id="languages"
                                            placeholder="Wpisz język i naciśnij Enter"
                                            tags={value}
                                            tagsHints={hints}
                                            useHintsToFindTag
                                            isReturnNewTag
                                            onClickAdd={handleAddLanguage}
                                            onClickDelete={handleRemoveLanguage}
                                            onEnterKeyDown={handleAddLanguage}
                                            enableEnterKeyDown
                                        />
                                    );
                                }}
                            />
                        </Box>
                    </Col>
                </Row>

                <Divider customStyle={styles.dividerFooter} />
                <ButtonGroup customStyle={styles.buttons}>
                    <Button
                        onClick={onClickCancel}
                        variant="text"
                        startIcon={<Icon name="close" width={12} height={12} />}
                    >
                        {t("ANULUJ")}
                    </Button>
                    <Button
                        type="submit"
                        startIcon={
                            <Icon
                                name="save"
                                width={14}
                                height={14}
                                fill={theme.colors.white}
                            />
                        }
                        disabled={loading}
                    >
                        {t("ZAPISZ")}
                    </Button>
                </ButtonGroup>
            </Form>
            <Loader show={loading} />
        </Paper>
    );
};

FormWorker.propTypes = {
    loading: PropTypes.bool,
    initialValues: PropTypes.object,
    professions: PropTypes.array,
    departments: PropTypes.array,
    onSubmit: PropTypes.func,
    onClickCancel: PropTypes.func,
};

export default FormWorker;
